<script lang="ts">
  import { month, year } from '../stores/appData';
  import TextArea from './TextArea.svelte';
  import Astrict from './Astrict.svelte';

  interface Props {
    day: number;
    html?: string;
    id?: string;
    event?: string;
    eventShort?: string;
    selected?: boolean;
    override?: boolean;
    disabled?: boolean;
    isPrevMonth?: boolean;
    isNextMonth?: boolean;
    onClick?: () => void;
    onDeleteValue?: (detail: { id: string; year: number }) => void;
    onUpdateValue?: (detail: { day: number; html: any; id?: string }) => void; // Added this prop
  }

  let {
    day,
    html,
    id,
    event = '',
    eventShort = '',
    selected = false,
    override = false,
    disabled = false,
    isPrevMonth = false,
    isNextMonth = false,
    onClick = () => {},
    onDeleteValue = () => {},
    onUpdateValue = () => {}, // Added this prop
  }: Props = $props();

  let date = $derived(new Date(new Date().setHours(0, 0, 0, 0)));
  let calDate = $derived(
    (() => {
      let d = new Date(`${$month} 1 ${$year}`);
      if (isPrevMonth) {
        d.setMonth(d.getMonth() - 1);
      } else if (isNextMonth) {
        d.setMonth(d.getMonth() + 1);
      }
      d.setDate(day);
      d.setHours(0, 0, 0, 0);
      return d;
    })(),
  );

  // is the calender date past today
  let past = $derived(calDate < date);

  function deleteValue() {
    if (id && onDeleteValue) {
      onDeleteValue({
        id,
        year: $year,
      });
    }
  }
</script>

<div
  class:selected
  class:override
  class:disabled
  class:past
  class:today={date.toDateString() === calDate.toDateString()}
  class="day-of-the-month calender-item"
  {id}
  onclick={onClick}
  onkeydown={e => e.key === 'Enter' && onClick && onClick()}
  role="button"
  tabindex="0"
  aria-label={`Day ${day}${event ? `, ${event}` : ''}`}
>
  <div class:override class="row">
    <p class="day" class:event>{day}</p>
    {#if event}
      <span class="event-group">
        <p class="event-title" class:override class:eventShort>{event}</p>
        <p aria-hidden="true" class="event-title-short" class:override class:eventShort>
          {eventShort}
        </p>
      </span>
    {/if}
    <button class="clear" onclick={deleteValue}>clear</button>
  </div>
  <TextArea {override} {html} {id} {day} {onUpdateValue} {disabled} />
  {#if html !== '' && html !== undefined}
    <Astrict event={!!event} {override} />
  {/if}
  {#if event}
    <Astrict hasEvent={!!event} {override} />
  {/if}
</div>

<style>
  .row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 1ch;
    align-items: center;
    font-weight: 600;
  }

  .disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  @media screen and (max-width: 768px) {
    .disabled {
      opacity: 0;
    }
  }

  .event-group {
    display: flex;
    gap: 0.5ch;
    justify-content: flex-end;
    align-items: center;
  }

  .event-title,
  .event-title-short {
    margin: 0;
    font-size: 0.8rem;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
  }

  @media screen and (max-width: 1500px) {
    .event-title {
      max-width: 65px;
    }
  }

  .event-title-short:empty {
    display: none;
  }

  @media screen and (max-width: 768px) {
    .row:not(.override) {
      grid-template-columns: 1fr;
      place-items: center;
    }
    .event-group:not(.override) {
      display: none;
    }
    .event-title-short:not(.override) {
      display: none;
    }
  }

  @media screen and (max-width: 1024px) {
    .event-title.eventShort {
      display: none;
    }

    .event-title-short.eventShort {
      display: block;
    }
  }

  button {
    color: var(--altColor);
    font-size: small;
    border: none;
    background: none;
    display: none;
  }

  button:active {
    background: none;
  }

  button:focus {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 25px;
  }

  .day-of-the-month {
    border-radius: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--altColor);
    display: grid;
    gap: 0.2rem;
    grid-template-rows: auto 1fr;
    color: var(--primaryDark);
    background-color: white;
    position: relative;
  }

  .day-of-the-month.override {
    height: 100%;
    padding: var(--gutter);
  }

  @media screen and (max-width: 768px) {
    .day-of-the-month {
      border: 1px solid white;
      gap: 0;
    }

    .day-of-the-month:not(.override).today {
      border-color: var(--primaryDark);
      box-shadow: 0 0 3px #00000099 inset;
    }

    .day-of-the-month:not(.override).selected {
      background-color: var(--altColor);
      color: var(--altText);
    }
  }

  @media screen and (max-width: 768px) {
    .day-of-the-month:not(.override) {
      grid-template-rows: auto;
      position: relative;
    }
    .day-of-the-month:not(.override):not(.today).past::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--holiday);
      clip-path: polygon(99% 0, 100% 2%, 1% 100%, 0 98%);
    }
  }

  .day-of-the-month:focus-within:not(.override) {
    border-color: var(--primaryDark);
    /* outline: 1px solid var(--primaryDark);
    outline-offset: 3px; */
    background-color: white;
  }

  .day-of-the-month:focus-within:not(.override) {
    box-shadow: 1px 10px 10px -10px #00000099;
    /* transform: scale(1.5); */
    z-index: 1;
  }

  @media screen and (min-width: 768px) {
    .day-of-the-month:focus-within .clear {
      display: block;
    }
  }

  .day {
    margin: 0;
    font-weight: bold;
  }
  .day.event {
    color: var(--primaryDark);
  }

  @media screen and (min-width: 551px) {
    .calender-item:nth-of-type(7n + 1):not(.override),
    .calender-item:nth-of-type(7n + 2):not(.override) {
      background-color: hsl(0, 0%, 95%);
    }
  }

  @media print {
    .calender-item {
      border-color: gray;
      background: transparent !important;
      border-radius: 3px;
    }
    p,
    .day.event {
      color: black;
      font-size: small;
    }
    p.event-title {
      color: black;
      font-size: 8px;
    }
    .event-title-short {
      display: none;
    }

    /* Added style for past dates in print mode */
    .calender-item.past::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: black !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      clip-path: polygon(
        0.5% 0%,
        0% 0.5%,
        99.5% 100%,
        100% 99.5%
      ); /* Diagonal line from top-left to bottom-right */
    }
  }
</style>
